/**
 * Ingredients + Ingredient Groups. Ports legacy GET /ingredients,
 * /ingredients/new, /ingredients/<id>/edit, /ingredients/<id>/delete,
 * /groups/*, /api/groups, /api/ingredients/<id>(/each-weight|/density),
 * /api/search (docs/parity/ingredients.md).
 *
 * Everything the list/form screens need is pre-computed server-side
 * (price_per_unit, cost_basis_complete, needs, density_assumed,
 * each_weight_missing, is_unused, group_deleted, and the segment `counts`),
 * so this file never re-derives those numbers: it only fetches, caches and
 * invalidates.
 *
 * GET /api/ingredients takes every filter the list offers (filter=,
 * no_group, group_id, search, sort) and they are all wired below. The list
 * screen itself fetches the whole kitchen once (`useIngredientsList({})`,
 * the same cache entry the ingredient form reads) and narrows it in the
 * browser with the same rules (features/ingredients/lib/sort.ts), so
 * switching All / Needs price / Unused or typing in the filter answers on the
 * next frame instead of after a round trip on a phone on the pass. `counts`
 * always covers the whole kitchen, so the segment numbers come from the
 * server either way.
 *
 * Tri-state trap (read before touching a form submit handler): package_size
 * and package_price are "was anything typed", not "is it non-zero". A raw
 * `Number(value) || null` idiom silently turns a confirmed-$0 item back into
 * "not set". Always go through `parseTriStateNumber` in lib/tristate.ts.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { isMine, registerLive } from '@/lib/live';

export type Ingredient = components['schemas']['IngredientOut'];
export type IngredientDetail = components['schemas']['IngredientDetailOut'];
export type IngredientListOut = components['schemas']['IngredientListOut'];
export type Group = components['schemas']['GroupOut'];
export type Consumer = components['schemas']['ConsumerOut'];
export type AllergenTag = components['schemas']['AllergenTagOut'];
export type IngredientPreviewIn = components['schemas']['IngredientPreviewIn'];
export type IngredientPreviewOut = components['schemas']['IngredientPreviewOut'];
export type IngredientCreateIn = components['schemas']['IngredientCreateIn'];
export type IngredientUpdateIn = components['schemas']['IngredientUpdateIn'];
export type SearchResult = components['schemas']['SearchResultOut'];
export type IngredientCounts = components['schemas']['IngredientCountsOut'];
/** Why an ingredient's own cost is an estimate, in fix order. */
export type IngredientNeed = Ingredient['needs'][number];
/** Where it is kept: walk_in, dry_store or freezer (null is not set). */
export type StorageArea = NonNullable<Ingredient['storage']>;
/** The server's ordering for GET /api/ingredients. The list screen sorts in
 *  the browser instead (features/ingredients/lib/sort.ts); this is for any
 *  caller that wants the server's order. */
export type ServerSortField =
  | 'name' | '-name'
  | 'group' | '-group'
  | 'supplier' | '-supplier'
  | 'pack_price' | '-pack_price'
  | 'unit_price' | '-unit_price'
  | 'used' | '-used'
  | 'newest';

/** GET /api/ingredients?filter=: needs_price is "its own cost is an
 *  estimate" (no price, no pack size, an assumed density or a missing each
 *  weight), the dashboard's unpriced-ingredients rule; needs_size is a subset. */
export type IngredientListFilter = 'all' | 'needs_price' | 'needs_size' | 'unused';

export type IngredientListFilters = {
  search?: string;
  filter?: IngredientListFilter;
  /** The legacy spelling of filter=unused; both may be given. */
  unusedOnly?: boolean;
  groupId?: number | null;
  /** Only items with no usable group (none set, or its group is in Trash). */
  noGroup?: boolean;
  sort?: ServerSortField;
  includeDeleted?: boolean;
};

const DOMAIN = 'ingredients' as const;

export const ingredientsKeys = {
  all: [DOMAIN] as const,
  list: (filters: IngredientListFilters) => [DOMAIN, 'list', filters] as const,
  detail: (id: number) => [DOMAIN, 'detail', id] as const,
  groups: (includeDeleted: boolean) => [DOMAIN, 'groups', includeDeleted] as const,
};

function listQuery(filters: IngredientListFilters) {
  return queryOptions({
    queryKey: ingredientsKeys.list(filters),
    queryFn: () =>
      unwrap(
        api.GET('/api/ingredients', {
          params: {
            query: {
              search: filters.search || undefined,
              filter: filters.filter && filters.filter !== 'all' ? filters.filter : undefined,
              unused_only: filters.unusedOnly || undefined,
              group_id: filters.groupId ?? undefined,
              no_group: filters.noGroup || undefined,
              sort: filters.sort,
              include_deleted: filters.includeDeleted || undefined,
            },
          },
        }),
      ),
  });
}

/** One fetch: live items (narrowed by whichever filters are given) plus the
 *  live groups and the whole-kitchen segment `counts`, and (when
 *  `includeDeleted`) the ingredients and groups in Trash. `{}` is the whole
 *  kitchen, shared by the list screen and the ingredient form. */
export function useIngredientsList(filters: IngredientListFilters) {
  return useQuery(listQuery(filters));
}

/** For the edit route's loader (context.queryClient.ensureQueryData), the
 *  same pattern _app.tsx and login.tsx use for the session query, so the
 *  form has data before it ever mounts. useIngredient below intentionally
 *  does NOT build on this via spread: `useQuery({ ...queryOptions(...),
 *  enabled })` loses the queryKey tuple's precise type and fails to
 *  typecheck (a real overload error hit independently in prep-lists.ts too),
 *  so it re-states the same queryKey/queryFn directly instead. */
export function ingredientDetailQuery(id: number) {
  return queryOptions({
    queryKey: ingredientsKeys.detail(id),
    queryFn: () => unwrap(api.GET('/api/ingredients/{ingredient_id}', { params: { path: { ingredient_id: id } } })),
  });
}

export function useIngredient(id: number | undefined) {
  return useQuery({
    queryKey: ingredientsKeys.detail(id ?? -1),
    queryFn: () => unwrap(api.GET('/api/ingredients/{ingredient_id}', { params: { path: { ingredient_id: id! } } })),
    enabled: id !== undefined,
  });
}

/** Cross-domain read owned by the Allergens domain, needed here only for the
 *  CREATE form's empty allergen toggle grid (the EDIT form doesn't need it:
 *  IngredientDetailOut.allergens already carries the full catalogue merged
 *  with this ingredient's status). Query key is namespaced 'allergens' (not
 *  'ingredients') so it happily shares a cache entry with whatever the
 *  Allergens domain's own screens fetch from the same endpoint. */
export function useAllergenCatalogue() {
  return useQuery({
    queryKey: ['allergens', 'catalogue'] as const,
    queryFn: () => unwrap(api.GET('/api/allergens')),
    staleTime: 5 * 60_000,
  });
}

/** Cross-domain read owned by the Units domain, needed here for the unit
 *  <select>s. Namespaced 'units' so it shares a cache entry with whatever
 *  the Units screen itself fetches. */
export function useUnitsList() {
  return useQuery({
    queryKey: ['units', 'list'] as const,
    queryFn: () => unwrap(api.GET('/api/units')),
    staleTime: 5 * 60_000,
  });
}

export function useGroups(includeDeleted = false) {
  return useQuery({
    queryKey: ingredientsKeys.groups(includeDeleted),
    queryFn: () => unwrap(api.GET('/api/ingredient-groups', { params: { query: { include_deleted: includeDeleted } } })),
  });
}

/** Ingredient half of the shared typeahead (GET /api/search), for the ingredients
 *  list's own uses and for any other domain's line/recipe picker. The parity spec
 *  calls this out explicitly as a shared endpoint recipes needs too, so it lives
 *  here rather than being re-implemented per screen. */
export async function searchIngredients(q: string, opts?: { excludeRecipe?: number }): Promise<SearchResult[]> {
  if (!q.trim()) return [];
  return unwrap(
    api.GET('/api/search', {
      params: { query: { q, type: 'ingredient', exclude_recipe: opts?.excludeRecipe ?? undefined } },
    }),
  );
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ingredientsKeys.all });
}

export function useCreateIngredient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IngredientCreateIn) => unwrap(api.POST('/api/ingredients', { body })),
    onSuccess: (data) => {
      qc.setQueryData(ingredientsKeys.detail(data.id), data);
      invalidateAll(qc);
    },
  });
}

export function useUpdateIngredient(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IngredientUpdateIn) =>
      unwrap(api.PATCH('/api/ingredients/{ingredient_id}', { params: { path: { ingredient_id: id } }, body })),
    onSuccess: (data) => {
      qc.setQueryData(ingredientsKeys.detail(id), data);
      invalidateAll(qc);
    },
  });
}

export function useDeleteIngredient(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      unwrap(
        api.DELETE('/api/ingredients/{ingredient_id}', { params: { path: { ingredient_id: id }, query: { version } } }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(ingredientsKeys.detail(id), data);
      invalidateAll(qc);
    },
  });
}

export function useRestoreIngredient(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      unwrap(
        api.POST('/api/ingredients/{ingredient_id}/restore', {
          params: { path: { ingredient_id: id }, query: { version } },
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(ingredientsKeys.detail(id), data);
      invalidateAll(qc);
    },
  });
}

/** Soft delete by id, for a list that moves many rows to Trash with one hook
 *  (useSoftDelete's `del`). Resolves with the deleted ingredient, whose
 *  `version` is the one Undo must send. */
export function useDeleteIngredientById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: number; version: number }) =>
      unwrap(
        api.DELETE('/api/ingredients/{ingredient_id}', { params: { path: { ingredient_id: id }, query: { version } } }),
      ),
    onSuccess: (data, { id }) => {
      qc.setQueryData(ingredientsKeys.detail(id), data);
      invalidateAll(qc);
    },
  });
}

/** Restore by id: useSoftDelete's Undo for a list row. */
export function useRestoreIngredientById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: number; version: number }) =>
      unwrap(
        api.POST('/api/ingredients/{ingredient_id}/restore', {
          params: { path: { ingredient_id: id }, query: { version } },
        }),
      ),
    onSuccess: (data, { id }) => {
      qc.setQueryData(ingredientsKeys.detail(id), data);
      invalidateAll(qc);
    },
  });
}

export function usePurgeIngredient(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      unwrap(
        api.DELETE('/api/ingredients/{ingredient_id}/purge', {
          params: { path: { ingredient_id: id }, query: { version } },
        }),
      ),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ingredientsKeys.detail(id) });
      invalidateAll(qc);
    },
  });
}

/** The two "chef-consent" quick-edits fired inline from a list row (and, in
 *  other domains, from the recipe editor mid-edit), with the same clamps as the form. */
export function useSetDensity(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { density: number; version: number }) =>
      unwrap(api.POST('/api/ingredients/{ingredient_id}/density', { params: { path: { ingredient_id: id } }, body: vars })),
    onSuccess: (data) => {
      qc.setQueryData(ingredientsKeys.detail(id), data);
      invalidateAll(qc);
    },
  });
}

export function useSetEachWeight(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { each_weight_g: number; version: number }) =>
      unwrap(
        api.POST('/api/ingredients/{ingredient_id}/each-weight', { params: { path: { ingredient_id: id } }, body: vars }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(ingredientsKeys.detail(id), data);
      invalidateAll(qc);
    },
  });
}

/** Live PPU maths while the chef is still typing, replacing legacy's calcPPU()
 *  JS duplicate of the pricing formulas. The form debounces ~250ms before
 *  calling mutate(); nothing here is persisted (server rolls back a SAVEPOINT). */
export function useIngredientPreview() {
  return useMutation({
    mutationFn: (body: IngredientPreviewIn) => unwrap(api.POST('/api/ingredients/preview', { body })),
  });
}

// ── Groups ───────────────────────────────────────────────────────────────

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => unwrap(api.POST('/api/ingredient-groups', { body: { name } })),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useRenameGroup(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { name: string; version: number }) =>
      unwrap(api.PATCH('/api/ingredient-groups/{group_id}', { params: { path: { group_id: id } }, body: vars })),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteGroup(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      unwrap(
        api.DELETE('/api/ingredient-groups/{group_id}', { params: { path: { group_id: id }, query: { version } } }),
      ),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useRestoreGroup(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      unwrap(
        api.POST('/api/ingredient-groups/{group_id}/restore', {
          params: { path: { group_id: id }, query: { version } },
        }),
      ),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Group delete by id, for a panel whose rows leave the list once deleted:
 *  the Undo in the toast outlives the row that asked for it. Resolves with
 *  the deleted group, whose `version` the restore must send. */
export function useDeleteGroupById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: number; version: number }) =>
      unwrap(
        api.DELETE('/api/ingredient-groups/{group_id}', { params: { path: { group_id: id }, query: { version } } }),
      ),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useRestoreGroupById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: number; version: number }) =>
      unwrap(
        api.POST('/api/ingredient-groups/{group_id}/restore', {
          params: { path: { group_id: id }, query: { version } },
        }),
      ),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Live updates ─────────────────────────────────────────────────────────
// ingredient_group.* invalidates the same domain key as ingredient.* because
// group_name/group_deleted are denormalized onto every ingredient row: a
// rename or delete changes what an ingredient row displays even though the
// ingredient's own version never bumps. `isMine` skips the refetch when this
// tab's own mutation already updated the cache in its onSuccess above;
// otherwise every save would trigger a redundant second fetch of its own echo.
registerLive('ingredient.', (evt, qc) => {
  if (isMine(evt)) return;
  invalidateAll(qc);
});
registerLive('ingredient_group.', (evt, qc) => {
  if (isMine(evt)) return;
  invalidateAll(qc);
});
